import { getRouteApi } from '@tanstack/react-router'
import CantileverCard from 'components/CantileverCard'
import { AssetBrief } from 'features/assetDatabase/components/assetBrief/AssetBrief'
import { AssetOverviewDataContext } from 'features/assetDatabase/contexts/AssetOverviewDataLayer'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
} from 'types'

import DetailsHeader from './DetailsHeader'
import { BrokenImgErrorMessage } from './imagesSection/BrokenImgErrorMessage'
import ImagesSection from './imagesSection/ImagesSection'
import InfoCard from './InfoCard'
import LocationCard from './LocationCard'
import { NotesCard } from './NotesCard'

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 1rem;
`

export const DetailsSection = React.forwardRef<HTMLDivElement>(
  (_props, ref) => {
    const { useParams } = getRouteApi('/assets/$siteID/$unitID')
    const { unitID } = useParams()
    const { hasPermissions } = useGeckoPermissions()
    const {
      unit,
      assetDataObj,
      refreshData,
      assetThumbnails,
      loadingThumbnails,
      refreshThumbnails,
      components,
      inspections,
    } = useContext(AssetOverviewDataContext)

    const [erroneousEntryId, setErrorMessageString] = useState<
      string | undefined
    >(undefined)
    const [needsEntryDeletion, setNeedsEntryDeletion] = useState<boolean>(false)

    const assetThumbnailPaths = assetThumbnails.map((assetThumbnail) => ({
      entry_id: assetThumbnail.id,
      path: assetThumbnail.id,
    }))

    const { urls: assetThumbnailURLs, errors: assetThumbnailErrors } =
      useRolodexSignedURLs(assetThumbnailPaths)

    const [primaryImageIndex, setPrimaryImageIndex] = useState<number | null>(
      null
    )

    useEffect(() => {
      if (
        assetThumbnailErrors[0] !== undefined &&
        typeof assetThumbnailErrors[0] === 'object' &&
        'message' in assetThumbnailErrors[0] &&
        assetThumbnailErrors[0].message.includes(
          'Issue grabbing rolodex storage signed URL for entry with id'
        )
      ) {
        setErrorMessageString(
          assetThumbnailErrors[0].message.match(/id=\[(.*?)\]/)?.[1] ||
            'unknown'
        )
        setNeedsEntryDeletion(true)
      }
    }, [assetThumbnailErrors])

    return (
      <DetailsContainer ref={ref}>
        <DetailsHeader
          noAssetModel={!assetDataObj?.assetModel}
          unit={unit}
          refreshData={refreshData}
        />
        <CardList>
          {unit && <AssetBrief unit={unit} refreshOverviewData={refreshData} />}
          <InfoCard />
          {assetThumbnailErrors[0] === undefined ? (
            <ImagesSection
              setPrimaryImageIndex={setPrimaryImageIndex}
              unit={unit}
              components={components}
              inspections={inspections}
              assetThumbnails={assetThumbnails}
              isLoading={loadingThumbnails}
              primaryImageIndex={primaryImageIndex}
              assetThumbnailURLs={assetThumbnailURLs}
              canUploadFiles={hasPermissions([
                PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
                PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
              ])}
              refreshData={refreshData}
              refreshThumbnails={refreshThumbnails}
              unitID={unitID}
            />
          ) : (
            <CantileverCard>
              <CantileverCard.CardHeader leadingContent='Images' />
              <CantileverCard.Section topPadding>
                <CantileverCard.Group>
                  <BrokenImgErrorMessage
                    erroneousEntryId={erroneousEntryId}
                    needsEntryDeletion={needsEntryDeletion}
                  />
                </CantileverCard.Group>
              </CantileverCard.Section>
            </CantileverCard>
          )}
          <NotesCard unit={unit} refreshData={refreshData} />
          <LocationCard unit={unit} refreshData={refreshData} />
        </CardList>
      </DetailsContainer>
    )
  }
)

DetailsSection.displayName = 'DetailsSection'
